import { config } from '../config';
import { API_ENDPOINTS } from '../constants/endpoints';
import { ChargebeeResponse, DownloadInvoiceResponse, InvoiceResponse } from '../types/chargebee';
import { get, post } from '../utils/api.utils';
import { decodeIdToken, decodeMetadataToken } from '../utils/token.utils';
import AuthService from './auth.service';

declare const Chargebee: any;
class ChargebeeService {
  public static initChargebee(site: string) {
    try {
    const cbInstance = Chargebee?.init({
        site: site,
        publishableKey: config.chargebee_publishableKey // todo - check key and dynamic
      });
      cbInstance.setPortalSession(() => {
        // https://apidocs.chargebee.com/docs/api/portal_sessions#create_a_portal_session

        const session = this.getPortalSession()
        return session
          
      });
      console.log('Chargebee initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Chargebee:', error);
    }
  }

  public static async getCustomerInvoices(customerId: string): Promise<InvoiceResponse> {
    try {
      const response = await get<InvoiceResponse>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_CUSTOMER_INVOICES(customerId)
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Customer not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to fetch customer invoices');
    }
  }

  public static async downloadInvoice(invoiceId: string): Promise<Blob> {
    try {
      const response = await get<DownloadInvoiceResponse>(
        API_ENDPOINTS.SUBSCRIPTIONS.DOWNLOAD_INVOICE(invoiceId)
      );
      
      const pdfResponse = await fetch(response.data.download_url);
      if (!pdfResponse.ok) {
        throw new Error('Failed to download invoice PDF');
      }
      
      return await pdfResponse.blob();
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Invoice not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to download invoice');
    }
  }

  public static async getPortalSession(): Promise<ChargebeeResponse> {
    try {
      const metadata = decodeMetadataToken();
      const customerId = metadata?.companies?.[0]?.id
      
      if (!customerId) {
        throw new Error('Customer ID not found in token');
      }

      const response = await get<ChargebeeResponse>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_PORTAL_SESSION(customerId)
      );

      if (!response.data?.id) {
        throw new Error('Invalid portal session response');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Detailed error:', error);
      throw error;
    }
  }

  public static async openBillingSection(): Promise<void> {
    try {
      var chargebeeInstance = Chargebee.getInstance();
      let cbPortal = chargebeeInstance.createChargebeePortal();
      cbPortal.openSection({
        sectionType: Chargebee.getPortalSections().ADDRESS,
        close: () => {
          console.log('Billing section closed');
        },
        loaded: () => {
          console.log('Billing section loaded');
        },
        error: (error: any) => {
          console.error('Portal error:', error);
        }
      })
    } catch (error) {
      console.error('Failed to open billing section:', error);
      throw error;
    }
  }

  public static async openPaymentSection(): Promise<void> {
    try {
      const decodedToken = decodeIdToken();
      const firstName = decodedToken?.given_name || "";
      const lastName = decodedToken?.family_name || "";

      var chargebeeInstance = Chargebee.getInstance();
      let cbPortal = chargebeeInstance.createChargebeePortal();
      cbPortal.openSection({
        sectionType: Chargebee.getPortalSections().PAYMENT_SOURCES,
        customer: {
          first_name: firstName,
          last_name: lastName
        },
        close: () => {
          console.log('Payment section closed');
        },
        loaded: () => {
          console.log('Payment section loaded');
        },
        error: (error: any) => {
          console.error('Portal error:', error);
        }
      })
    } catch (error) {
      console.error('Failed to open payment section:', error);
      throw error;
    }
  }

  public static async getSubscriptionDetails(customerId: string): Promise<any> {
    try {
      const response = await get<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_SUBSCRIPTION_DETAILS(customerId)
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Subscription not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to fetch subscription details');
    }
  }

  public static async upgradePlanUsingCheckout(subscriptionId: string, planId: string, units?: number, trialEnd?: number): Promise<any> {
    try {
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.UPGRADE_PLAN,
        {
          subscriptionId,
          planId,
          units,
          trialEnd
        }
      );
      
      // Call refresh token if upgrade was successful
      if (response.status === 200) {
        await AuthService.refreshToken();
      }
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Subscription not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to upgrade subscription plan');
    }
  }

  public static async getPlans(): Promise<any> {
    try {
      const response = await get<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_PLANS
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Plans not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to fetch plans');
    }
  }

  public static async getPlanById(planId: string): Promise<any> {
    try {
      const response = await get<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_PLAN_BY_ID(planId)
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Plan not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to fetch plan details');
    }
  }

  public static async validateCoupon(couponCode: string, planId?: string): Promise<any> {
    try {
        const endpoint = planId 
            ? API_ENDPOINTS.SUBSCRIPTIONS.VALIDATE_COUPON(couponCode, planId)
            : API_ENDPOINTS.SUBSCRIPTIONS.VALIDATE_COUPON(couponCode);
            
        const response = await get<any>(endpoint);
        return response;
    } catch (error: any) {
        if (error.response?.status === 404) {
            throw new Error('Coupon not found');
        }
        if (error.response?.status === 401) {
            throw new Error('Unauthorized access');
        }
        
        throw new Error('Failed to validate coupon');
    }
  }

  public static async addPaymentSource(customerId: string, cardToken: string): Promise<any> {
    try {
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.ADD_PAYMENT_SOURCE,
        {
          customerId,
          cardToken
        }
      );
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Customer not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid payment information');
      }
      
      throw new Error('Failed to add payment source');
    }
  }

  public static async createSubscription(customerId: string, planId: string, couponId?: string, planUnits?: number): Promise<any> {
    try {
      const payload = {
        customerId,
        planId,
        ...(couponId && { couponId }),
        ...(planUnits && { planUnits })
      };
      
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.CREATE_SUBSCRIPTION,
        payload
      );
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Customer or plan not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid subscription information');
      }
      
      throw new Error('Failed to create subscription');
    }
  }

  public static async changePlan(subscriptionId: string, planId: string, units: number): Promise<any> {
    try {
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.CHANGE_PLAN,
        {
          subscriptionId,
          planId,
          units
        }
      );
      
      // Call refresh token if plan change was successful
      if (response.status === 200) {
        await AuthService.refreshToken();
      }
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Subscription or plan not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid plan change information');
      }
      
      throw new Error('Failed to change subscription plan');
    }
  }

  public static async getBillingDetails(customerId: string): Promise<ChargebeeResponse> {
    try {
      const response = await get<ChargebeeResponse>(
        API_ENDPOINTS.SUBSCRIPTIONS.GET_BILLING_DETAILS(customerId)
      );
      
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Customer billing details not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      throw new Error('Failed to fetch billing details');
    }
  }

  public static async cancelSubscription(subscriptionId: string): Promise<ChargebeeResponse> {
    try {
      const response = await post<any>(
        API_ENDPOINTS.SUBSCRIPTIONS.CANCEL_SUBSCRIPTION(subscriptionId)
      );
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Subscription not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      if (error.response?.status === 400) {
        throw new Error('Invalid cancellation request');
      }
      
      throw new Error('Failed to cancel subscription');
    }
  }

  public static async openEditSubscriptionSection(subscriptionId: string): Promise<void> {
    try {
      const chargebeeInstance = Chargebee.getInstance();
      const cbPortal = chargebeeInstance.createChargebeePortal();
      const callbacks = {
        close: async () => {
          console.log('Edit subscription section closed');
          // Refresh token when the edit subscription section is closed
          try {
            await AuthService.refreshToken();
            console.log('Token refreshed after subscription edit');
          } catch (refreshError) {
            console.error('Failed to refresh token after subscription edit:', refreshError);
          }
        },
        loaded: () => {
          console.log('Edit subscription section loaded');
        },
        error: (error: any) => {
          console.error('Portal error:', error);
        }
      };
      
      cbPortal.open(callbacks, {
        sectionType: Chargebee.getPortalSections().EDIT_SUBSCRIPTION,
        params: {
          subscriptionId: subscriptionId
        }
      });
    } catch (error) {
      console.error('Failed to open edit subscription section:', error);
      throw error;
    }
  }
}

export default ChargebeeService;
